import React, { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useController } from 'react-hook-form'
import clsx from 'clsx'
import { useDebounceCallback } from 'usehooks-ts'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import Dropdown from '@divvy-web/skylab.dropdown'
import { FormDropdown } from '@divvy-web/skylab.form'
import FormField from '@divvy-web/skylab.formfield'
import ListItem from '@divvy-web/skylab.listitem'
import Spinner from '@divvy-web/skylab.spinner'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'

import { useGetNaicsCodeLazyQuery } from './GetNaicsCode.gql'
import styles from './styles.module.scss'

const IndustryCombobox = ({
  control,
  disabled,
  readOnly,
}: Omit<React.ComponentProps<typeof FormDropdown>, 'name' | 'options'>) => {
  const [getClassName] = useNameFormatter('IndustryCombobox')
  const {
    field: { value: naicsCode, onChange: onNaicsCodeChange },
  } = useController({ control, name: 'naicsCode' })
  const {
    field: { value: industry, onChange: onIndustryChange },
  } = useController({ control, name: 'industry' })
  const { formatMessage } = useIntl()

  const [inputValue, setInputValue] = useState('')
  const onIndustryUpdate = useEffectEvent((industry: string) => {
    setInputValue(industry)
  })

  const [getNaicsCodes, { loading, data, error }] = useGetNaicsCodeLazyQuery()

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load data for Industry dropdown'
          id='sputnik.IndustryCombobox__Jplef2'
        />
      ),
    },
  })

  const options = useMemo(() => {
    if (!data?.naicsCodes?.length) {
      return []
    }

    return data.naicsCodes.map((item) => ({
      label: item.title || '',
      value: item.naicsCode || '',
    }))
  }, [data])

  const fetchIndustryOptions = useCallback(
    async (query: string) => {
      if (!query) {
        return
      }

      await getNaicsCodes({
        variables: {
          query: query ?? '',
        },
      })
    },
    [getNaicsCodes],
  )

  const debouncedFetchIndustryOptions = useDebounceCallback(fetchIndustryOptions, 300)

  useEffect(() => {
    fetchIndustryOptions(industry)
    onIndustryUpdate(industry)
  }, [fetchIndustryOptions, industry])

  const renderDropdownItem =
    ({ getItemProps, highlightedIndex }) =>
    (item: { label: string; value: string }, index: number) => {
      return (
        <ListItem
          className='fs-unmask'
          isHighlighted={highlightedIndex === index}
          isSelected={naicsCode === item.value}
          label={item.label}
          value={item.value}
          {...getItemProps({
            key: item.value,
            index,
            item,
          })}
        />
      )
    }

  const renderDropdownContent = (dropdownItemProps) => {
    if (loading) {
      return (
        <ListItem label>
          <Spinner centered />
        </ListItem>
      )
    }

    if (!inputValue) {
      return (
        <ListItem
          className='fs-unmask'
          label={formatMessage({
            defaultMessage:
              "Please enter a value and select a value that most closely matches your business's industry",
            id: 'sputnik.IndustryCombobox__P2XPFt',
          })}
        />
      )
    }

    if (!options.length) {
      return (
        <ListItem
          className='fs-unmask'
          label={formatMessage({
            defaultMessage: 'We did not find a value for this industry. Please type a different value.',
            id: 'sputnik.IndustryCombobox__fgey/O',
          })}
        />
      )
    }

    return options.map(renderDropdownItem(dropdownItemProps))
  }

  return (
    <FormField
      className={clsx(styles[getClassName('')], 'fs-unmask')}
      inputId='naicsCode'
      label={
        <FormattedMessage
          defaultMessage='Industry'
          id='sputnik.IndustryCombobox__TqlgrX'
        />
      }
    >
      <Dropdown
        isPortal
        isSearchable
        className='fs-unmask'
        dataTestId='naicsCode'
        disabled={disabled}
        inputProps={{
          id: 'industry-combobox',
        }}
        inputValue={inputValue}
        isMultiSelect={false}
        placeholder={
          <FormattedMessage
            defaultMessage='Enter here'
            id='sputnik.IndustryCombobox__kQAf2d'
          />
        }
        readOnly={readOnly}
        value={{
          value: naicsCode,
          label: industry,
        }}
        onInputValueChanged={(value) => {
          setInputValue(value)
          if (value.length) {
            debouncedFetchIndustryOptions(value)
          }
        }}
        onSelect={(item) => {
          setInputValue(item?.label ?? '')
          onNaicsCodeChange(item?.value)
          onIndustryChange(item?.label)
        }}
      >
        {renderDropdownContent}
      </Dropdown>
    </FormField>
  )
}

export default IndustryCombobox
